import React, { useState } from "react";
import { Col, message, Row, Space } from "antd";
import { PlusOutlined, SearchOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import TablePagination from "../../../components/Table/TablePagination";
import { color, icon } from "../../../resource";
import SortFieldCustom from "../../../components/SortFieldTable/SortFieldCustom";
import { Texts } from "../../GeneralSettingPage/orderTimeFrame/OrderPeriod.styles";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import Input from "../../../components/Input/Input";
import { RangePicker } from "../../../components/DatePicker/DatePicker";
import StatusBadge from "../../../components/Badge/StatusBadge";
import { ActionBtn } from "../../../components/Button/TableActionButton";
import { CreditLimitStatusLabel } from "../../../definitions/creditLimit";
import ConfirmModal from "../../../components/Modal/ConfirmModal";
import moment from "moment";
import { numberFormatter } from "../../../utility/Formatter";
import { checkTodayInRange } from "../../../utility/checkTodayInRange";
import type { AllCreditLimitEntity } from "../../../entities/CreditLimitEntity";
import { normalizeStatus, STATUS_CREDIT } from "../../../definitions/creditStatus";
import { deleteTempCreditLimit } from "../../../datasource/CreditLimitDatasource";

interface Props {
  data: AllCreditLimitEntity;
  sortField: any;
  sortDirection: any;
  handleSortChange: any;
  searchText: any;
  setSearchText: any;
  filterDate: any;
  setFilterDate: any;
  status: any;
  setStatus: any;
  page: any;
  setPage: any;
  loading: any;
  setModalCheck: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      data: {
        orderNo: string;
        customerName: string;
      } | null;
    }>
  >;
  modalCheck: {
    open: boolean;
    data: object | null;
  };
  pendingCount: number;
  fetchData: () => Promise<void>;
}

function CreditTab({
  data,
  sortField,
  sortDirection,
  handleSortChange,
  searchText,
  setSearchText,
  filterDate,
  setFilterDate,
  status,
  setStatus,
  page,
  setPage,
  loading,
  fetchData,
  setModalCheck,
  pendingCount,
}: Props) {
  const navigate = useNavigate();
  const [modalDelete, setModalDelete] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });

  const columns: any = [
    {
      title: () => (
        <SortFieldCustom
          title='วันที่ส่งคำขอ'
          sortDirection={sortField === "createdAt" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("createdAt")}
        />
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <>
              <Texts size='16'>{moment(value).format("DD/MM/YYYY, HH:mm")}</Texts>
            </>
          ),
        };
      },
    },
    {
      title: "รหัสคำขอ",
      dataIndex: "temporaryCreditLimitCode",
      key: "temporaryCreditLimitCode",
      width: 100,
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <>
              <Texts size='16'>{value || "-"}</Texts>
            </>
          ),
        };
      },
    },
    {
      title: "ชื่อร้านค้า",
      dataIndex: "customerCompany",
      key: "customerCompany",
      width: "20%",
      ellipsis: true,
      render: (value: any, row: any, index: number) => {
        const cusCompName = row.customerCompany?.customerName;
        const cusCompTax = row.customerCompany?.customer?.taxNo;
        return {
          children: (
            <>
              <Texts size='16'>{cusCompName}</Texts>
              <Texts style={{ color: color.background4 }}>{cusCompTax || "-"}</Texts>
            </>
          ),
        };
      },
    },
    {
      title: "ระยะเวลาการใช้งาน",
      dataIndex: "time",
      key: "time",
      width: 160,
      render: (value: any, row: any, index: number) => {
        const calculateDays = (startDate, endDate) => {
          if (!startDate || !endDate) return "";
          const days = moment(endDate).diff(moment(startDate), "days");
          return days > 0 ? `${days} วัน` : "";
        };
        return {
          children: (
            <>
              <Texts size='16'>{`${
                row.startDate ? moment(row.startDate).format("DD/MM/YYYY") : ""
              } - ${row.endDate ? moment(row.endDate).format("DD/MM/YYYY") : ""}`}</Texts>
              <Text fontFamily='Sarabun' fontSize={14} color='Disable'>
                {calculateDays(row.startDate, row.endDate)}
              </Text>
            </>
          ),
        };
      },
    },
    {
      title: () => (
        <SortFieldCustom
          title='วงเงินที่ขอ'
          sortDirection={sortField === "creditLimitRequest" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("creditLimitRequest")}
        />
      ),
      dataIndex: "creditLimitRequest",
      key: "creditLimitRequest",
      width: 130,
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <>
              <Texts size='16'>฿ {numberFormatter(value, 2)}</Texts>
              <Texts style={{ color: color.background4 }}>
                {numberFormatter(row.creditLimitRequestPercent, 2)} %
              </Texts>
            </>
          ),
        };
      },
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (value: any, row: any, index: number) => {
        const checkDate = checkTodayInRange(row.startDate, row.endDate);
        const normalizedStatus = normalizeStatus(row.status);
        const meta = STATUS_CREDIT[normalizedStatus] || {};

        return {
          children: (
            <Space direction='vertical' size={4}>
              <StatusBadge label={meta.label || "-"} bgColor={meta.color || "gray"} />

              {row.status === "APPROVED" &&
                (checkDate === "Earlier" ? (
                  <StatusBadge
                    key={index}
                    label='หมดอายุ'
                    bgColor={STATUS_CREDIT[normalizeStatus("REJECTED")].color}
                  />
                ) : (
                  <StatusBadge
                    key={index + 1}
                    label={STATUS_CREDIT[String(row.isActive)].label}
                    bgColor={STATUS_CREDIT[String(row.isActive)].color}
                  />
                ))}
            </Space>
          ),
        };
      },
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      width: status === "REJECTED" ? 80 : 160,
      render: (value: any, row: any, index: number) => {
        const checkDate = checkTodayInRange(row.startDate, row.endDate) === "Earlier";
        const disabled = row.status === "APPROVED" && checkDate;
        return {
          children: (
            <Row gutter={10} style={{ gap: 6 }} justify='start'>
              <ActionBtn
                onClick={() => {
                  navigate(`/order/check-credit-limit/${row.customerCompanyId}`, {
                    state: { tempId: row.temporaryCreditLimitId },
                  });
                }}
                icon={<UnorderedListOutlined style={{ width: 20, height: 20, fontWeight: 700 }} />}
              />
              {/* <ActionBtn
                onClick={() =>
                  setModalCheck({
                    open: true,
                    data: {
                      orderNo: row.orderNo,
                      customerName: row.customerCompany?.customerName,
                    },
                  })
                }
                icon={<img src={icon.Audit} style={{ width: 20, height: 20, fontWeight: 700 }} />}
              /> */}
              {row.status !== "REJECTED" && (
                <ActionBtn
                  disabled={disabled}
                  onClick={() =>
                    disabled
                      ? undefined
                      : navigate(`/order/request-credit-limit/edit/${row.customerCompanyId}`, {
                          state: { tempId: row.temporaryCreditLimitId },
                        })
                  }
                  icon={
                    <img
                      src={icon.writeIcon}
                      style={{
                        cursor: disabled ? "no-drop" : "pointer",
                        width: 20,
                        height: 20,
                        fontWeight: 700,
                        filter: disabled ? "grayscale(100%)" : "none",
                        opacity: disabled ? 0.5 : 1,
                      }}
                    />
                  }
                />
              )}

              {row.status === "WAITING" && (
                <ActionBtn
                  onClick={() => setModalDelete({ open: true, id: row.temporaryCreditLimitId })}
                  icon={
                    <img src={icon.delete} style={{ width: 20, height: 20, fontWeight: 700 }} />
                  }
                />
              )}
            </Row>
          ),
        };
      },
    },
  ];
  return (
    <>
      <Row gutter={8} style={{ margin: "12px 0px" }}>
        {[
          {
            key: "WAITING",
            label: `รออนุมัติ (${numberFormatter(pendingCount, 0) ?? 0})`,
          },
          { key: "APPROVED", label: `อนุมัติแล้ว` },
          { key: "REJECTED", label: `ไม่อนุมัติ` },
        ].map(({ key, label }) => (
          <Col key={key} span={2}>
            <Button
              title={label}
              typeButton={key === status ? "navy-blue" : "light-navy-blue"}
              onClick={() => setStatus(key)}
            />
          </Col>
        ))}
      </Row>
      <Row justify='space-between' gutter={[8, 8]} style={{ marginBottom: "12px" }}>
        <Col className='gutter-row' span={16}>
          <Input
            placeholder='ค้นหาชื่อร้านค้า / เลขนิติบุคคล / รหัสคำขอ'
            suffix={<SearchOutlined />}
            defaultValue={searchText}
            onPressEnter={(e) => {
              const value = (e.target as HTMLTextAreaElement).value;
              setSearchText(value);
              setPage(1);
            }}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;
              if (!value) {
                setSearchText(undefined);
                setPage(1);
              }
            }}
          />
        </Col>
        <Col className='gutter-row' span={4}>
          <RangePicker
            allowEmpty={[true, true]}
            enablePast
            value={filterDate}
            onChange={(dates, dateString) => {
              setFilterDate(dates);
              setPage(1);
            }}
          />
        </Col>
        <Col className='gutter-row' span={4}>
          <Button
            onClick={() => navigate(`/order/request-credit-limit/create`)}
            title='คำขอวงเงินชั่วคราว'
            icon={<PlusOutlined style={{ color: color.white }} />}
          />
        </Col>
      </Row>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "end",
          marginBottom: "8px",
        }}
      >
        <Text fontSize={14}>ทั้งหมด {numberFormatter(data?.count || 0, 0)} รายการ</Text>
      </div>
      <TablePagination
        isLoading={loading}
        columns={columns}
        data={data?.data || []}
        pagination={{
          current: page,
          pageSize: 10,
          total: data?.count || 0,
          onChange: (page) => {
            setPage(page);
          },
        }}
        onPageChange={(pageNum) => setPage(pageNum)}
        locale={{
          emptyText: (
            <div
              style={{
                padding: 32,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 24,
                minHeight: 350,
              }}
            >
              <img
                src={icon.emptyHistoryPoint}
                style={{
                  width: 80,
                  height: 90,
                }}
                alt='empty_table'
              />
              <Text fontFamily='Sarabun' fontSize={14} fontWeight={400} color='Text3'>
                ไม่มีรายการคำสั่งซื้อ
              </Text>
            </div>
          ),
        }}
      />
      <ConfirmModal
        visible={modalDelete.open}
        onConfirm={async () => {
          try {
            const profile = localStorage.getItem("profile");
            const userProfile = profile ? JSON.parse(profile) : null;
            const { firstname, lastname } = userProfile;
            const fullName = `${firstname} ${lastname}`;
            if (modalDelete?.id) {
              const res = await deleteTempCreditLimit(modalDelete?.id, fullName);
              if (res.success) {
                message.success("ลบข้อมูลสำเร็จ");
                setModalDelete({ open: false, id: null });
                await fetchData();
              } else {
                message.error("เกิดข้อผิดพลาดในการลบ");
              }
            }
          } catch (error) {
            console.error("Error deleting temp credit limit:", error);
            message.error("เกิดข้อผิดพลาดในการลบ");
          }
        }}
        onCancel={() => {
          setModalDelete({ open: false, id: null });
        }}
        title='ต้องการลบคำขอวงเงินชั่วคราว'
        desc={`โปรดยืนยันการลบข้อมูลคำขอวงเงินชั่วคราว`}
      />
    </>
  );
}

export default CreditTab;
